import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Bell, Filter, Download, CheckCircle, XCircle, AlertTriangle, 
  Clock, TrendingUp, Settings, Plus, Edit2, Trash2, X, RefreshCw,
  Volume2, VolumeX, MapPin, Battery, Wifi, Car, Gauge, Thermometer,
  Fuel, Shield, AlertOctagon, Navigation, Eye, EyeOff, Search,
  ChevronDown, ChevronUp, MoreVertical, Copy, ExternalLink,
  Zap, Activity, Radio, Send, History, Layers, Target, 
  PauseCircle, PlayCircle, BarChart3, PieChart, Calendar,
  Smartphone, Mail, MessageSquare, BellRing, BellOff, Sliders,
  FileText, Archive, RotateCcw, CheckCheck, XOctagon, Info
} from 'lucide-react';
import './alarmdashboard.css';

// Import RTK Hooks
import {
  useGetAlarmsQuery,
  useGetAlarmStatsQuery,
  useGetAlarmRulesQuery,
  useGetDeviceAlarmHistoryQuery,
  useCreateAlarmRuleMutation,
  useUpdateAlarmRuleMutation,
  useDeleteAlarmRuleMutation,
  useAcknowledgeAlarmMutation,
  useResolveAlarmMutation,
} from '../redux/admin/alarmApi';
import { allSounds } from '../assets/sounds';

// Alarm Type Configurations
const ALARM_TYPE_CONFIG = {
  // Movement Alarms
  speeding: { icon: Gauge, color: '#ef4444', label: 'Speeding', category: 'movement' },
  harsh_acceleration: { icon: Zap, color: '#f97316', label: 'Harsh Acceleration', category: 'movement' },
  harsh_braking: { icon: AlertOctagon, color: '#f97316', label: 'Harsh Braking', category: 'movement' },
  harsh_cornering: { icon: Navigation, color: '#f97316', label: 'Harsh Cornering', category: 'movement' },
  
  // Security Alarms
  sos_alarm: { icon: AlertOctagon, color: '#dc2626', label: 'SOS Emergency', category: 'security' },
  panic_button: { icon: Shield, color: '#dc2626', label: 'Panic Button', category: 'security' },
  tampering: { icon: Shield, color: '#dc2626', label: 'Tampering', category: 'security' },
  external_power_cut: { icon: Zap, color: '#dc2626', label: 'Power Cut', category: 'security' },
  vibration_alarm: { icon: Activity, color: '#eab308', label: 'Vibration', category: 'security' },
  towing_alarm: { icon: Car, color: '#dc2626', label: 'Towing Detected', category: 'security' },
  unauthorized_movement: { icon: Car, color: '#dc2626', label: 'Unauthorized Movement', category: 'security' },
  
  // Geofence Alarms
  geofence_entry: { icon: Target, color: '#3b82f6', label: 'Geofence Entry', category: 'geofence' },
  geofence_exit: { icon: Target, color: '#8b5cf6', label: 'Geofence Exit', category: 'geofence' },
  geofence_violation: { icon: Target, color: '#ef4444', label: 'Geofence Violation', category: 'geofence' },
  
  // Status Alarms
  low_battery: { icon: Battery, color: '#eab308', label: 'Low Battery', category: 'status' },
  power_off: { icon: Zap, color: '#6b7280', label: 'Power Off', category: 'status' },
  power_on: { icon: Zap, color: '#22c55e', label: 'Power On', category: 'status' },
  acc_on: { icon: Car, color: '#22c55e', label: 'ACC On', category: 'status' },
  acc_off: { icon: Car, color: '#6b7280', label: 'ACC Off', category: 'status' },
  ignition_on: { icon: Car, color: '#22c55e', label: 'Ignition On', category: 'status' },
  ignition_off: { icon: Car, color: '#6b7280', label: 'Ignition Off', category: 'status' },
  device_offline: { icon: Wifi, color: '#ef4444', label: 'Device Offline', category: 'status' },
  gps_signal_lost: { icon: Radio, color: '#eab308', label: 'GPS Signal Lost', category: 'status' },
  
  // Maintenance Alarms
  overspeed_alarm: { icon: Gauge, color: '#ef4444', label: 'Overspeed', category: 'maintenance' },
  idle_too_long: { icon: PauseCircle, color: '#eab308', label: 'Idle Too Long', category: 'maintenance' },
  fuel_drop: { icon: Fuel, color: '#f97316', label: 'Fuel Drop', category: 'maintenance' },
  fuel_theft: { icon: Fuel, color: '#dc2626', label: 'Fuel Theft', category: 'maintenance' },
  temperature_high: { icon: Thermometer, color: '#ef4444', label: 'High Temperature', category: 'maintenance' },
  temperature_low: { icon: Thermometer, color: '#3b82f6', label: 'Low Temperature', category: 'maintenance' },
  tire_pressure_low: { icon: Activity, color: '#eab308', label: 'Low Tire Pressure', category: 'maintenance' },
  
  // OBD Alarms
  dtc_detected: { icon: AlertTriangle, color: '#f97316', label: 'DTC Detected', category: 'obd' },
  engine_fault: { icon: AlertOctagon, color: '#ef4444', label: 'Engine Fault', category: 'obd' },
  check_engine: { icon: Car, color: '#eab308', label: 'Check Engine', category: 'obd' },
  
  // Driver Behavior
  fatigue_driving: { icon: Eye, color: '#8b5cf6', label: 'Fatigue Driving', category: 'behavior' },
  distraction: { icon: Smartphone, color: '#f97316', label: 'Distraction', category: 'behavior' },
  no_seat_belt: { icon: Shield, color: '#eab308', label: 'No Seat Belt', category: 'behavior' },
  
  // Custom
  custom_alarm: { icon: Bell, color: '#6b7280', label: 'Custom Alarm', category: 'custom' },
};

const SEVERITY_CONFIG = {
  critical: { color: '#dc2626', bg: '#fef2f2', label: 'Critical', priority: 1 },
  high: { color: '#ef4444', bg: '#fef2f2', label: 'High', priority: 2 },
  medium: { color: '#f97316', bg: '#fff7ed', label: 'Medium', priority: 3 },
  low: { color: '#eab308', bg: '#fefce8', label: 'Low', priority: 4 },
  info: { color: '#3b82f6', bg: '#eff6ff', label: 'Info', priority: 5 },
};

const NOTIFICATION_CHANNELS = [
  { id: 'socket', label: 'Real-time', icon: Zap },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'push', label: 'Push', icon: Smartphone },
];

const AlarmDashboard = () => {
  // State Management
  const [activeTab, setActiveTab] = useState('monitoring');
  const [filters, setFilters] = useState({ 
    status: 'active', 
    severity: 'all',
    alarmType: 'all',
    category: 'all',
    search: '',
    dateRange: 'today'
  });
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showAlarmDetailModal, setShowAlarmDetailModal] = useState(false);
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [selectedAlarms, setSelectedAlarms] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'cards', 'timeline'
  const [sortConfig, setSortConfig] = useState({ key: 'triggeredAt', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Advanced Rule Form State
  const [ruleForm, setRuleForm] = useState({
    name: '',
    alarmType: 'speeding',
    severity: 'medium',
    priority: 1,
    isActive: true,
    scope: 'global',
    deviceIds: [],
    userIds: [],
    conditions: {
      speedThreshold: 80,
      batteryThreshold: 20,
      temperatureMin: -10,
      temperatureMax: 60,
      idleDuration: 300,
      fuelDropThreshold: 10,
      geofenceIds: []
    },
    notifications: {
      enabled: true,
      channels: ['socket'],
      sound: 'default',
      repeatInterval: 0,
      maxRepeats: 3
    },
    autoActions: {
      autoAcknowledge: false,
      autoResolve: false,
      autoResolveAfter: 3600,
      sendCommand: ''
    }
  });

  // RTK Queries
  const { 
    data: alarms = [], 
    isLoading: alarmsLoading, 
    isFetching: alarmsFetching,
    refetch: refetchAlarms 
  } = useGetAlarmsQuery(filters, {
    pollingInterval: autoRefresh ? refreshInterval * 1000 : 0
  });
  
  const { data: stats = [], refetch: refetchStats } = useGetAlarmStatsQuery();
  const { data: rules = [], isLoading: rulesLoading, refetch: refetchRules } = useGetAlarmRulesQuery();

  // RTK Mutations
  const [createRule, { isLoading: creatingRule }] = useCreateAlarmRuleMutation();
  const [updateRule, { isLoading: updatingRule }] = useUpdateAlarmRuleMutation();
  const [deleteRule, { isLoading: deletingRule }] = useDeleteAlarmRuleMutation();
  const [ackAlarm, { isLoading: ackingAlarm }] = useAcknowledgeAlarmMutation();
  const [resAlarm, { isLoading: resolvingAlarm }] = useResolveAlarmMutation();

  // Computed Stats
  const computedStats = useMemo(() => {
    const activeAlarms = alarms.filter(a => a.status === 'active');
    const criticalCount = activeAlarms.filter(a => a.severity === 'critical').length;
    const highCount = activeAlarms.filter(a => a.severity === 'high').length;
    const acknowledgedCount = alarms.filter(a => a.status === 'acknowledged').length;
    const resolvedToday = alarms.filter(a => {
      if (a.status !== 'resolved') return false;
      const today = new Date();
      const resolvedDate = new Date(a.resolvedAt);
      return resolvedDate.toDateString() === today.toDateString();
    }).length;

    const byCategory = {};
    Object.keys(ALARM_TYPE_CONFIG).forEach(type => {
      const category = ALARM_TYPE_CONFIG[type].category;
      if (!byCategory[category]) byCategory[category] = 0;
      byCategory[category] += activeAlarms.filter(a => a.alarmType === type).length;
    });

    return {
      total: activeAlarms.length,
      critical: criticalCount,
      high: highCount,
      acknowledged: acknowledgedCount,
      resolvedToday,
      byCategory
    };
  }, [alarms]);

  // Sorted & Filtered Alarms
  const processedAlarms = useMemo(() => {
    let result = [...alarms];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(a => 
        a.deviceName?.toLowerCase().includes(searchLower) ||
        a.imei?.includes(searchLower) ||
        a.alarmType?.toLowerCase().includes(searchLower) ||
        a.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (filters.category !== 'all') {
      result = result.filter(a => 
        ALARM_TYPE_CONFIG[a.alarmType]?.category === filters.category
      );
    }

    // Alarm Type filter
    if (filters.alarmType !== 'all') {
      result = result.filter(a => a.alarmType === filters.alarmType);
    }

    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'severity') {
        aVal = SEVERITY_CONFIG[a.severity]?.priority || 5;
        bVal = SEVERITY_CONFIG[b.severity]?.priority || 5;
      }

      if (sortConfig.key === 'triggeredAt') {
        aVal = new Date(a.triggeredAt).getTime();
        bVal = new Date(b.triggeredAt).getTime();
      }

      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [alarms, filters, sortConfig]);

useEffect(() => {
  if (soundEnabled && computedStats.critical > 0) {
    const audio = new Audio(allSounds.sosAlarm); // ✅ Use imported sound
    audio.volume = 0.8;
    audio.play().catch(() => {});
  }
}, [computedStats.critical, soundEnabled]);

  // Handlers
  const handleRuleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRule) {
        await updateRule({ ruleId: editingRule._id, ...ruleForm }).unwrap();
      } else {
        await createRule(ruleForm).unwrap();
      }
      setShowRuleModal(false);
      setEditingRule(null);
      resetRuleForm();
    } catch (err) {
      alert("Error saving rule: " + (err.data?.msg || err.message || "Failed"));
    }
  };

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      alarmType: 'speeding',
      severity: 'medium',
      priority: 1,
      isActive: true,
      scope: 'global',
      deviceIds: [],
      userIds: [],
      conditions: {
        speedThreshold: 80,
        batteryThreshold: 20,
        temperatureMin: -10,
        temperatureMax: 60,
        idleDuration: 300,
        fuelDropThreshold: 10,
        geofenceIds: []
      },
      notifications: {
        enabled: true,
        channels: ['socket'],
        sound: 'default',
        repeatInterval: 0,
        maxRepeats: 3
      },
      autoActions: {
        autoAcknowledge: false,
        autoResolve: false,
        autoResolveAfter: 3600,
        sendCommand: ''
      }
    });
  };

  const handleAcknowledge = async (id, notes = "Acknowledged from Dashboard") => {
    try {
      await ackAlarm({ alarmId: id, notes }).unwrap();
    } catch (err) {
      alert("Failed to acknowledge: " + (err.data?.msg || err.message));
    }
  };

  const handleResolve = async (id, notes = "Resolved from Dashboard") => {
    try {
      await resAlarm({ alarmId: id, notes }).unwrap();
    } catch (err) {
      alert("Failed to resolve: " + (err.data?.msg || err.message));
    }
  };

  const handleBulkAcknowledge = async () => {
    for (const id of selectedAlarms) {
      await handleAcknowledge(id, "Bulk acknowledged");
    }
    setSelectedAlarms([]);
    setShowBulkActionsModal(false);
  };

  const handleBulkResolve = async () => {
    for (const id of selectedAlarms) {
      await handleResolve(id, "Bulk resolved");
    }
    setSelectedAlarms([]);
    setShowBulkActionsModal(false);
  };

  const handleDeleteRule = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        await deleteRule(ruleId).unwrap();
      } catch (err) {
        alert("Failed to delete: " + (err.data?.msg || err.message));
      }
    }
  };

  const handleExport = (format) => {
    const data = processedAlarms.map(a => ({
      Device: a.deviceName,
      IMEI: a.imei,
      Type: ALARM_TYPE_CONFIG[a.alarmType]?.label || a.alarmType,
      Severity: a.severity,
      Status: a.status,
      'Triggered At': new Date(a.triggeredAt).toLocaleString(),
      Location: a.location ? `${a.location.latitude}, ${a.location.longitude}` : 'N/A',
      Notes: a.notes || ''
    }));

    if (format === 'csv') {
      const headers = Object.keys(data[0] || {}).join(',');
      const rows = data.map(row => Object.values(row).join(','));
      const csv = [headers, ...rows].join('\n');
      downloadFile(csv, 'alarms-export.csv', 'text/csv');
    } else if (format === 'json') {
      downloadFile(JSON.stringify(data, null, 2), 'alarms-export.json', 'application/json');
    }
    setShowExportModal(false);
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleRowExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const toggleAlarmSelection = (id) => {
    setSelectedAlarms(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const selectAllAlarms = () => {
    if (selectedAlarms.length === processedAlarms.length) {
      setSelectedAlarms([]);
    } else {
      setSelectedAlarms(processedAlarms.map(a => a._id));
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const openAlarmDetail = (alarm) => {
    setSelectedAlarm(alarm);
    setShowAlarmDetailModal(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      alarmType: rule.alarmType,
      severity: rule.severity || 'medium',
      priority: rule.priority || 1,
      isActive: rule.isActive !== false,
      scope: rule.scope || 'global',
      deviceIds: rule.deviceIds || [],
      userIds: rule.userIds || [],
      conditions: { ...ruleForm.conditions, ...rule.conditions },
      notifications: { ...ruleForm.notifications, ...rule.notifications },
      autoActions: { ...ruleForm.autoActions, ...rule.autoActions }
    });
    setShowRuleModal(true);
  };

  // Render Functions
  const renderAlarmIcon = (alarmType) => {
    const config = ALARM_TYPE_CONFIG[alarmType] || { icon: Bell, color: '#6b7280' };
    const Icon = config.icon;
    return <Icon size={18} style={{ color: config.color }} />;
  };

  const renderSeverityBadge = (severity) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
    return (
      <span 
        className="ald-severity-badge"
        style={{ 
          backgroundColor: config.bg, 
          color: config.color,
          borderColor: config.color 
        }}
      >
        {config.label}
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#ef4444', bg: '#fef2f2', icon: AlertTriangle },
      acknowledged: { color: '#eab308', bg: '#fefce8', icon: Eye },
      resolved: { color: '#22c55e', bg: '#f0fdf4', icon: CheckCircle },
      dismissed: { color: '#6b7280', bg: '#f3f4f6', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    return (
      <span 
        className="ald-status-badge"
        style={{ backgroundColor: config.bg, color: config.color }}
      >
        <Icon size={12} />
        {status}
      </span>
    );
  };

  return (
    <div className="ald-container">
      {/* Header Section */}
      <div className="ald-header-section">
        <div className="ald-title-area">
          <div className="ald-title-row">
            <Bell className="ald-title-icon" size={28} />
            <div>
              <h2>Alarm Operations Center</h2>
              <p>Real-time vehicle health monitoring & incident management</p>
            </div>
          </div>
          <div className="ald-header-actions">
            <button 
              className={`ald-icon-btn ${soundEnabled ? 'active' : ''}`}
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? 'Mute Alerts' : 'Enable Alerts'}
            >
              {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <button 
              className={`ald-icon-btn ${autoRefresh ? 'active' : ''}`}
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? 'Disable Auto-refresh' : 'Enable Auto-refresh'}
            >
              <RefreshCw size={18} className={autoRefresh && alarmsFetching ? 'spinning' : ''} />
            </button>
            <button 
              className="ald-icon-btn"
              onClick={() => setShowExportModal(true)}
              title="Export Data"
            >
              <Download size={18} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="ald-nav-tabs">
          <button 
            className={`ald-tab-trigger ${activeTab === 'monitoring' ? 'ald-active' : ''}`} 
            onClick={() => setActiveTab('monitoring')}
          >
            <Activity size={16} />
            Live Monitoring
          </button>
          <button 
            className={`ald-tab-trigger ${activeTab === 'rules' ? 'ald-active' : ''}`} 
            onClick={() => setActiveTab('rules')}
          >
            <Sliders size={16} />
            Rules Engine
          </button>
          <button 
            className={`ald-tab-trigger ${activeTab === 'analytics' ? 'ald-active' : ''}`} 
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart3 size={16} />
            Analytics
          </button>
          <button 
            className={`ald-tab-trigger ${activeTab === 'history' ? 'ald-active' : ''}`} 
            onClick={() => setActiveTab('history')}
          >
            <History size={16} />
            History
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="ald-stats-row">
        <div className="ald-stat-card critical">
          <div className="ald-stat-info">
            <span className="ald-stat-label">Critical Alarms</span>
            <span className="ald-stat-value">{computedStats.critical}</span>
          </div>
          <div className="ald-stat-icon-bg">
            <AlertOctagon size={24} />
          </div>
          {computedStats.critical > 0 && <div className="ald-stat-pulse" />}
        </div>
        
        <div className="ald-stat-card warning">
          <div className="ald-stat-info">
            <span className="ald-stat-label">High Priority</span>
            <span className="ald-stat-value">{computedStats.high}</span>
          </div>
          <div className="ald-stat-icon-bg">
            <AlertTriangle size={24} />
          </div>
        </div>
        
        <div className="ald-stat-card info">
          <div className="ald-stat-info">
            <span className="ald-stat-label">Active Total</span>
            <span className="ald-stat-value">{computedStats.total}</span>
          </div>
          <div className="ald-stat-icon-bg">
            <Bell size={24} />
          </div>
        </div>
        
        <div className="ald-stat-card pending">
          <div className="ald-stat-info">
            <span className="ald-stat-label">Acknowledged</span>
            <span className="ald-stat-value">{computedStats.acknowledged}</span>
          </div>
          <div className="ald-stat-icon-bg">
            <Eye size={24} />
          </div>
        </div>
        
        <div className="ald-stat-card success">
          <div className="ald-stat-info">
            <span className="ald-stat-label">Resolved Today</span>
            <span className="ald-stat-value">{computedStats.resolvedToday}</span>
          </div>
          <div className="ald-stat-icon-bg">
            <CheckCircle size={24} />
          </div>
        </div>
      </div>

      {/* Monitoring Tab */}
      {activeTab === 'monitoring' && (
        <>
          {/* Advanced Filter Bar */}
          <div className="ald-filter-section">
            <div className="ald-search-box">
              <Search size={18} />
              <input 
                type="text"
                placeholder="Search by device, IMEI, or notes..."
                value={filters.search}
                onChange={e => setFilters({...filters, search: e.target.value})}
              />
              {filters.search && (
                <X 
                  size={16} 
                  className="ald-search-clear"
                  onClick={() => setFilters({...filters, search: ''})}
                />
              )}
            </div>

            <div className="ald-filter-group">
              <Filter size={16} />
              <select 
                value={filters.status} 
                onChange={e => setFilters({...filters, status: e.target.value})}
              >
                <option value="active">Active</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
                <option value="all">All Status</option>
              </select>

              <select 
                value={filters.severity} 
                onChange={e => setFilters({...filters, severity: e.target.value})}
              >
                <option value="all">All Severity</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select 
                value={filters.category} 
                onChange={e => setFilters({...filters, category: e.target.value})}
              >
                <option value="all">All Categories</option>
                <option value="security">Security</option>
                <option value="movement">Movement</option>
                <option value="geofence">Geofence</option>
                <option value="status">Status</option>
                <option value="maintenance">Maintenance</option>
                <option value="obd">OBD</option>
                <option value="behavior">Behavior</option>
              </select>

              <select 
                value={filters.alarmType} 
                onChange={e => setFilters({...filters, alarmType: e.target.value})}
              >
                <option value="all">All Types</option>
                {Object.entries(ALARM_TYPE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            <div className="ald-view-controls">
              <button 
                className={`ald-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table View"
              >
                <Layers size={16} />
              </button>
              <button 
                className={`ald-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Card View"
              >
                <PieChart size={16} />
              </button>
            </div>

            <div className="ald-action-buttons">
              {selectedAlarms.length > 0 && (
                <button 
                  className="ald-btn-secondary"
                  onClick={() => setShowBulkActionsModal(true)}
                >
                  <CheckCheck size={16} />
                  Bulk Actions ({selectedAlarms.length})
                </button>
              )}
              <button 
                className="ald-btn-primary"
                onClick={() => { refetchAlarms(); refetchStats(); }}
                disabled={alarmsFetching}
              >
                <RefreshCw size={16} className={alarmsFetching ? 'spinning' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {/* Alarms Table */}
          <div className="ald-data-paper">
            {viewMode === 'table' ? (
              <div className="ald-table-wrapper">
                <table className="ald-main-table">
                  <thead>
                    <tr>
                      <th className="ald-checkbox-col">
                        <input 
                          type="checkbox"
                          checked={selectedAlarms.length === processedAlarms.length && processedAlarms.length > 0}
                          onChange={selectAllAlarms}
                        />
                      </th>
                      <th onClick={() => handleSort('triggeredAt')} className="sortable">
                        Time
                        {sortConfig.key === 'triggeredAt' && (
                          sortConfig.direction === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />
                        )}
                      </th>
                      <th>Device</th>
                      <th>Type</th>
                      <th onClick={() => handleSort('severity')} className="sortable">
                        Severity
                        {sortConfig.key === 'severity' && (
                          sortConfig.direction === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />
                        )}
                      </th>
                      <th>Status</th>
                      <th>Location</th>
                      <th>Data</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarmsLoading ? (
                      <tr>
                        <td colSpan="9" className="ald-loading-cell">
                          <div className="ald-spinner" />
                          Loading alarms...
                        </td>
                      </tr>
                    ) : processedAlarms.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="ald-empty-cell">
                          <Bell size={48} />
                          <p>No alarms found</p>
                          <span>Adjust your filters or check back later</span>
                        </td>
                      </tr>
                    ) : (
                      processedAlarms.map(alarm => (
                        <React.Fragment key={alarm._id}>
                          <tr className={`ald-alarm-row ${alarm.severity}`}>
                            <td>
                              <input 
                                type="checkbox"
                                checked={selectedAlarms.includes(alarm._id)}
                                onChange={() => toggleAlarmSelection(alarm._id)}
                              />
                            </td>
                            <td>
                              <div className="ald-time-cell">
                                <span className="ald-time-ago">{getTimeAgo(alarm.triggeredAt)}</span>
                                <span className="ald-time-full">
                                  {new Date(alarm.triggeredAt).toLocaleString()}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="ald-device-cell">
                                <strong>{alarm.deviceName || 'Unknown Device'}</strong>
                                <span className="ald-imei">{alarm.imei}</span>
                              </div>
                            </td>
                            <td>
                              <div className="ald-type-cell">
                                {renderAlarmIcon(alarm.alarmType)}
                                <span>{ALARM_TYPE_CONFIG[alarm.alarmType]?.label || alarm.alarmType}</span>
                              </div>
                            </td>
                            <td>{renderSeverityBadge(alarm.severity)}</td>
                            <td>{renderStatusBadge(alarm.status)}</td>
                            <td>
                              {alarm.location ? (
                                <button 
                                  className="ald-location-btn"
                                  onClick={() => window.open(`https://maps.google.com/?q=${alarm.location.latitude},${alarm.location.longitude}`, '_blank')}
                                >
                                  <MapPin size={14} />
                                  View
                                </button>
                              ) : (
                                <span className="ald-no-data">N/A</span>
                              )}
                            </td>
                            <td>
                              <button 
                                className="ald-expand-btn"
                                onClick={() => toggleRowExpand(alarm._id)}
                              >
                                {expandedRows.has(alarm._id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                {alarm.data?.speed && `${alarm.data.speed} km/h`}
                                {alarm.data?.battery && `${alarm.data.battery}%`}
                              </button>
                            </td>
                            <td>
                              <div className="ald-action-cell">
                                <button
                                  className="ald-action-btn view"
                                  onClick={() => openAlarmDetail(alarm)}
                                  title="View Details"
                                >
                                  <Eye size={16} />
                                </button>
                                {alarm.status === 'active' && (
                                  <button
                                    className="ald-action-btn ack"
                                    onClick={() => handleAcknowledge(alarm._id)}
                                    disabled={ackingAlarm}
                                    title="Acknowledge"
                                  >
                                    <CheckCircle size={16} />
                                  </button>
                                )}
                                {alarm.status !== 'resolved' && (
                                  <button
                                    className="ald-action-btn resolve"
                                    onClick={() => handleResolve(alarm._id)}
                                    disabled={resolvingAlarm}
                                    title="Resolve"
                                  >
                                    <XCircle size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedRows.has(alarm._id) && (
                            <tr className="ald-expanded-row">
                              <td colSpan="9">
                                <div className="ald-expanded-content">
                                  <div className="ald-expanded-grid">
                                    {alarm.data?.speed && (
                                      <div className="ald-data-item">
                                        <Gauge size={16} />
                                        <span>Speed: {alarm.data.speed} km/h</span>
                                        {alarm.data.speedLimit && (
                                          <span className="ald-limit">(Limit: {alarm.data.speedLimit})</span>
                                        )}
                                      </div>
                                    )}
                                    {alarm.data?.battery && (
                                      <div className="ald-data-item">
                                        <Battery size={16} />
                                        <span>Battery: {alarm.data.battery}%</span>
                                      </div>
                                    )}
                                    {alarm.data?.temperature && (
                                      <div className="ald-data-item">
                                        <Thermometer size={16} />
                                        <span>Temperature: {alarm.data.temperature}°C</span>
                                      </div>
                                    )}
                                    {alarm.data?.fuelLevel && (
                                      <div className="ald-data-item">
                                        <Fuel size={16} />
                                        <span>Fuel: {alarm.data.fuelLevel}%</span>
                                      </div>
                                    )}
                                    {alarm.data?.geofenceName && (
                                      <div className="ald-data-item">
                                        <Target size={16} />
                                        <span>Geofence: {alarm.data.geofenceName}</span>
                                      </div>
                                    )}
                                    {alarm.data?.dtcCodes?.length > 0 && (
                                      <div className="ald-data-item">
                                        <AlertTriangle size={16} />
                                        <span>DTC Codes: {alarm.data.dtcCodes.join(', ')}</span>
                                      </div>
                                    )}
                                    {alarm.location?.address && (
                                      <div className="ald-data-item full-width">
                                        <MapPin size={16} />
                                        <span>Address: {alarm.location.address}</span>
                                      </div>
                                    )}
                                    {alarm.notes && (
                                      <div className="ald-data-item full-width">
                                        <FileText size={16} />
                                        <span>Notes: {alarm.notes}</span>
                                      </div>
                                    )}
                                  </div>
                                  {alarm.acknowledgedAt && (
                                    <div className="ald-timeline-info">
                                      <span>Acknowledged: {new Date(alarm.acknowledgedAt).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {alarm.resolvedAt && (
                                    <div className="ald-timeline-info">
                                      <span>Resolved: {new Date(alarm.resolvedAt).toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Card View */
              <div className="ald-cards-grid">
                {processedAlarms.map(alarm => (
                  <div 
                    key={alarm._id} 
                    className={`ald-alarm-card ${alarm.severity}`}
                    onClick={() => openAlarmDetail(alarm)}
                  >
                    <div className="ald-card-header">
                      <div className="ald-card-icon">
                        {renderAlarmIcon(alarm.alarmType)}
                      </div>
                      <div className="ald-card-title">
                        <h4>{ALARM_TYPE_CONFIG[alarm.alarmType]?.label || alarm.alarmType}</h4>
                        <span>{alarm.deviceName}</span>
                      </div>
                      {renderSeverityBadge(alarm.severity)}
                    </div>
                    <div className="ald-card-body">
                      <div className="ald-card-meta">
                        <Clock size={14} />
                        <span>{getTimeAgo(alarm.triggeredAt)}</span>
                      </div>
                      {alarm.data?.speed && (
                        <div className="ald-card-data">
                          <Gauge size={14} />
                          <span>{alarm.data.speed} km/h</span>
                        </div>
                      )}
                    </div>
                    <div className="ald-card-footer">
                      {renderStatusBadge(alarm.status)}
                      <div className="ald-card-actions">
                        {alarm.status === 'active' && (
                          <button onClick={(e) => { e.stopPropagation(); handleAcknowledge(alarm._id); }}>
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {alarm.status !== 'resolved' && (
                          <button onClick={(e) => { e.stopPropagation(); handleResolve(alarm._id); }}>
                            <XCircle size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="ald-data-paper">
          <div className="ald-section-header">
            <div>
              <h3>Alarm Rules Configuration</h3>
              <p>Define conditions, thresholds, and automated responses</p>
            </div>
            <button 
              className="ald-btn-primary" 
              onClick={() => { 
                setEditingRule(null); 
                resetRuleForm(); 
                setShowRuleModal(true); 
              }}
            >
              <Plus size={16} />
              Create New Rule
            </button>
          </div>

          <div className="ald-rules-grid">
            {rulesLoading ? (
              <div className="ald-loading-state">
                <div className="ald-spinner" />
                Loading rules...
              </div>
            ) : rules.length === 0 ? (
              <div className="ald-empty-state">
                <Sliders size={48} />
                <h4>No rules configured</h4>
                <p>Create your first alarm rule to automate responses</p>
                <button 
                  className="ald-btn-primary"
                  onClick={() => { 
                    setEditingRule(null); 
                    resetRuleForm(); 
                    setShowRuleModal(true); 
                  }}
                >
                  <Plus size={16} />
                  Create Rule
                </button>
              </div>
            ) : (
              rules.map(rule => (
                <div key={rule._id} className={`ald-rule-card ${!rule.isActive ? 'inactive' : ''}`}>
                  <div className="ald-rule-header">
                    <div className="ald-rule-icon">
                      {renderAlarmIcon(rule.alarmType)}
                    </div>
                    <div className="ald-rule-title">
                      <h4>{rule.name}</h4>
                      <span className="ald-rule-type">
                        {ALARM_TYPE_CONFIG[rule.alarmType]?.label || rule.alarmType}
                      </span>
                    </div>
                    <div className="ald-rule-status">
                      {rule.isActive ? (
                        <span className="ald-active-badge">
                          <PlayCircle size={12} /> Active
                        </span>
                      ) : (
                        <span className="ald-inactive-badge">
                          <PauseCircle size={12} /> Paused
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="ald-rule-body">
                    <div className="ald-rule-conditions">
                      <h5>Conditions</h5>
                      <div className="ald-condition-tags">
                        {rule.conditions?.speedThreshold && (
                          <span className="ald-condition-tag">
                            <Gauge size={12} />
                            Speed {'>'} {rule.conditions.speedThreshold} km/h
                          </span>
                        )}
                        {rule.conditions?.batteryThreshold && (
                          <span className="ald-condition-tag">
                            <Battery size={12} />
                            Battery {'<'} {rule.conditions.batteryThreshold}%
                          </span>
                        )}
                        {rule.conditions?.idleDuration && (
                          <span className="ald-condition-tag">
                            <Clock size={12} />
                            Idle {'>'} {Math.floor(rule.conditions.idleDuration / 60)}min
                          </span>
                        )}
                        {rule.conditions?.temperatureMax && (
                          <span className="ald-condition-tag">
                            <Thermometer size={12} />
                            Temp {'>'} {rule.conditions.temperatureMax}°C
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ald-rule-notifications">
                      <h5>Notifications</h5>
                      <div className="ald-channel-icons">
                        {rule.notifications?.channels?.map(channel => {
                          const channelConfig = NOTIFICATION_CHANNELS.find(c => c.id === channel);
                          const Icon = channelConfig?.icon || Bell;
                          return (
                            <span key={channel} className="ald-channel-icon" title={channelConfig?.label}>
                              <Icon size={14} />
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {rule.autoActions?.autoResolve && (
                      <div className="ald-rule-auto">
                        <span className="ald-auto-badge">
                          <RotateCcw size={12} />
                          Auto-resolve after {Math.floor(rule.autoActions.autoResolveAfter / 60)}min
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="ald-rule-footer">
                    <span className="ald-rule-scope">
                      Scope: {rule.scope || 'Global'}
                    </span>
                    <div className="ald-rule-actions">
                      <button 
                        className="ald-rule-btn edit"
                        onClick={() => openEditRule(rule)}
                        title="Edit Rule"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button 
                        className="ald-rule-btn delete"
                        onClick={() => handleDeleteRule(rule._id)}
                        disabled={deletingRule}
                        title="Delete Rule"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="ald-analytics-section">
          <div className="ald-analytics-grid">
            <div className="ald-analytics-card">
              <h4>Alarms by Category</h4>
              <div className="ald-category-chart">
                {Object.entries(computedStats.byCategory).map(([category, count]) => (
                  <div key={category} className="ald-category-bar">
                    <span className="ald-category-label">{category}</span>
                    <div className="ald-bar-container">
                      <div 
                        className="ald-bar-fill" 
                        style={{ 
                          width: `${(count / computedStats.total) * 100}%`,
                          backgroundColor: category === 'security' ? '#dc2626' : 
                                          category === 'movement' ? '#f97316' :
                                          category === 'geofence' ? '#3b82f6' :
                                          category === 'status' ? '#22c55e' :
                                          '#6b7280'
                        }}
                      />
                    </div>
                    <span className="ald-category-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="ald-analytics-card">
              <h4>Active Rules</h4>
              <div className="ald-metric-large">
                <span className="ald-metric-value">{rules.filter(r => r.isActive).length}</span>
                <span className="ald-metric-label">of {rules.length} rules active</span>
              </div>
            </div>

            <div className="ald-analytics-card">
              <h4>Response Time</h4>
              <div className="ald-metric-large">
                <span className="ald-metric-value">--</span>
                <span className="ald-metric-label">avg minutes to acknowledge</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="ald-data-paper">
          <div className="ald-section-header">
            <h3>Alarm History</h3>
            <div className="ald-date-range">
              <Calendar size={16} />
              <select 
                value={filters.dateRange}
                onChange={e => setFilters({...filters, dateRange: e.target.value})}
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
          {/* Reuse the table with resolved filter */}
          <div className="ald-table-wrapper">
            {/* Same table structure but filtered for resolved */}
            <p className="ald-info-text">
              <Info size={16} />
              Switch to "Resolved" status filter in Monitoring tab to view historical alarms
            </p>
          </div>
        </div>
      )}

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="ald-modal-backdrop" onClick={() => setShowRuleModal(false)}>
          <div className="ald-modal-box large" onClick={e => e.stopPropagation()}>
            <div className="ald-modal-header">
              <h3>{editingRule ? 'Edit Alarm Rule' : 'Create Alarm Rule'}</h3>
              <button className="ald-modal-close" onClick={() => setShowRuleModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleRuleSubmit} className="ald-rule-form">
              <div className="ald-form-tabs">
                <div className="ald-form-section">
                  <h4>Basic Information</h4>
                  
                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>Rule Name *</label>
                      <input 
                        type="text"
                        required
                        value={ruleForm.name}
                        onChange={e => setRuleForm({...ruleForm, name: e.target.value})}
                        placeholder="e.g., High Speed Alert"
                      />
                    </div>
                    <div className="ald-form-group">
                      <label>Priority</label>
                      <input 
                        type="number"
                        min="1"
                        max="10"
                        value={ruleForm.priority}
                        onChange={e => setRuleForm({...ruleForm, priority: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>Alarm Type *</label>
                      <select 
                        value={ruleForm.alarmType}
                        onChange={e => setRuleForm({...ruleForm, alarmType: e.target.value})}
                      >
                        {Object.entries(ALARM_TYPE_CONFIG).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ald-form-group">
                      <label>Default Severity</label>
                      <select 
                        value={ruleForm.severity}
                        onChange={e => setRuleForm({...ruleForm, severity: e.target.value})}
                      >
                        {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>Scope</label>
                      <select 
                        value={ruleForm.scope}
                        onChange={e => setRuleForm({...ruleForm, scope: e.target.value})}
                      >
                        <option value="global">Global (All Devices)</option>
                        <option value="device">Specific Devices</option>
                        <option value="user">Specific Users</option>
                        <option value="group">Device Groups</option>
                      </select>
                    </div>
                    <div className="ald-form-group checkbox">
                      <label>
                        <input 
                          type="checkbox"
                          checked={ruleForm.isActive}
                          onChange={e => setRuleForm({...ruleForm, isActive: e.target.checked})}
                        />
                        Rule Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="ald-form-section">
                  <h4>Conditions & Thresholds</h4>

                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>
                        <Gauge size={14} />
                        Speed Threshold (km/h)
                      </label>
                      <input 
                        type="number"
                        value={ruleForm.conditions.speedThreshold}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          conditions: {...ruleForm.conditions, speedThreshold: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                    <div className="ald-form-group">
                      <label>
                        <Battery size={14} />
                        Battery Threshold (%)
                      </label>
                      <input 
                        type="number"
                        value={ruleForm.conditions.batteryThreshold}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          conditions: {...ruleForm.conditions, batteryThreshold: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                  </div>

                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>
                        <Thermometer size={14} />
                        Min Temperature (°C)
                      </label>
                      <input 
                        type="number"
                        value={ruleForm.conditions.temperatureMin}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          conditions: {...ruleForm.conditions, temperatureMin: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                    <div className="ald-form-group">
                      <label>
                        <Thermometer size={14} />
                        Max Temperature (°C)
                      </label>
                      <input 
                        type="number"
                        value={ruleForm.conditions.temperatureMax}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          conditions: {...ruleForm.conditions, temperatureMax: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                  </div>

                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>
                        <Clock size={14} />
                        Idle Duration (seconds)
                      </label>
                      <input 
                        type="number"
                        value={ruleForm.conditions.idleDuration}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          conditions: {...ruleForm.conditions, idleDuration: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                    <div className="ald-form-group">
                      <label>
                        <Fuel size={14} />
                        Fuel Drop Threshold (%)
                      </label>
                      <input 
                        type="number"
                        value={ruleForm.conditions.fuelDropThreshold}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          conditions: {...ruleForm.conditions, fuelDropThreshold: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div className="ald-form-section">
                  <h4>Notifications</h4>

                  <div className="ald-form-row">
                    <div className="ald-form-group checkbox">
                      <label>
                        <input 
                          type="checkbox"
                          checked={ruleForm.notifications.enabled}
                          onChange={e => setRuleForm({
                            ...ruleForm, 
                            notifications: {...ruleForm.notifications, enabled: e.target.checked}
                          })}
                        />
                        Enable Notifications
                      </label>
                    </div>
                  </div>

                  <div className="ald-form-group">
                    <label>Notification Channels</label>
                    <div className="ald-channel-selector">
                      {NOTIFICATION_CHANNELS.map(channel => (
                        <label key={channel.id} className="ald-channel-option">
                          <input 
                            type="checkbox"
                            checked={ruleForm.notifications.channels.includes(channel.id)}
                            onChange={e => {
                              const channels = e.target.checked
                                ? [...ruleForm.notifications.channels, channel.id]
                                : ruleForm.notifications.channels.filter(c => c !== channel.id);
                              setRuleForm({
                                ...ruleForm,
                                notifications: {...ruleForm.notifications, channels}
                              });
                            }}
                          />
                          <channel.icon size={16} />
                          {channel.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="ald-form-row">
                    <div className="ald-form-group">
                      <label>Alert Sound</label>
                      <select 
                        value={ruleForm.notifications.sound}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          notifications: {...ruleForm.notifications, sound: e.target.value}
                        })}
                      >
                        <option value="default">Default</option>
                        <option value="urgent">Urgent</option>
                        <option value="warning">Warning</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div className="ald-form-group">
                      <label>Repeat Interval (seconds, 0=no repeat)</label>
                      <input 
                        type="number"
                        min="0"
                        value={ruleForm.notifications.repeatInterval}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          notifications: {...ruleForm.notifications, repeatInterval: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div className="ald-form-section">
                  <h4>Automated Actions</h4>

                  <div className="ald-form-row">
                    <div className="ald-form-group checkbox">
                      <label>
                        <input 
                          type="checkbox"
                          checked={ruleForm.autoActions.autoAcknowledge}
                          onChange={e => setRuleForm({
                            ...ruleForm, 
                            autoActions: {...ruleForm.autoActions, autoAcknowledge: e.target.checked}
                          })}
                        />
                        Auto-acknowledge alarm
                      </label>
                    </div>
                    <div className="ald-form-group checkbox">
                      <label>
                        <input 
                          type="checkbox"
                          checked={ruleForm.autoActions.autoResolve}
                          onChange={e => setRuleForm({
                            ...ruleForm, 
                            autoActions: {...ruleForm.autoActions, autoResolve: e.target.checked}
                          })}
                        />
                        Auto-resolve alarm
                      </label>
                    </div>
                  </div>

                  {ruleForm.autoActions.autoResolve && (
                    <div className="ald-form-group">
                      <label>Auto-resolve after (seconds)</label>
                      <input 
                        type="number"
                        min="60"
                        value={ruleForm.autoActions.autoResolveAfter}
                        onChange={e => setRuleForm({
                          ...ruleForm, 
                          autoActions: {...ruleForm.autoActions, autoResolveAfter: parseInt(e.target.value)}
                        })}
                      />
                    </div>
                  )}

                  <div className="ald-form-group">
                    <label>
                      <Send size={14} />
                      Send Device Command (optional)
                    </label>
                    <input 
                      type="text"
                      placeholder="e.g., ENGINE_OFF, BEEP"
                      value={ruleForm.autoActions.sendCommand}
                      onChange={e => setRuleForm({
                        ...ruleForm, 
                        autoActions: {...ruleForm.autoActions, sendCommand: e.target.value}
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="ald-modal-footer">
                <button 
                  type="button" 
                  className="ald-btn-secondary"
                  onClick={() => setShowRuleModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="ald-btn-primary"
                  disabled={creatingRule || updatingRule}
                >
                  {(creatingRule || updatingRule) ? (
                    <>
                      <div className="ald-spinner-sm" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      {editingRule ? 'Update Rule' : 'Create Rule'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alarm Detail Modal */}
      {showAlarmDetailModal && selectedAlarm && (
        <div className="ald-modal-backdrop" onClick={() => setShowAlarmDetailModal(false)}>
          <div className="ald-modal-box" onClick={e => e.stopPropagation()}>
            <div className="ald-modal-header">
              <div className="ald-detail-header">
                {renderAlarmIcon(selectedAlarm.alarmType)}
                <h3>{ALARM_TYPE_CONFIG[selectedAlarm.alarmType]?.label || selectedAlarm.alarmType}</h3>
                {renderSeverityBadge(selectedAlarm.severity)}
              </div>
              <button className="ald-modal-close" onClick={() => setShowAlarmDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="ald-detail-content">
              <div className="ald-detail-section">
                <h4>Device Information</h4>
                <div className="ald-detail-grid">
                  <div className="ald-detail-item">
                    <span className="ald-detail-label">Device Name</span>
                    <span className="ald-detail-value">{selectedAlarm.deviceName}</span>
                  </div>
                  <div className="ald-detail-item">
                    <span className="ald-detail-label">IMEI</span>
                    <span className="ald-detail-value">{selectedAlarm.imei}</span>
                  </div>
                </div>
              </div>

              <div className="ald-detail-section">
                <h4>Alarm Details</h4>
                <div className="ald-detail-grid">
                  <div className="ald-detail-item">
                    <span className="ald-detail-label">Status</span>
                    {renderStatusBadge(selectedAlarm.status)}
                  </div>
                  <div className="ald-detail-item">
                    <span className="ald-detail-label">Triggered At</span>
                    <span className="ald-detail-value">
                      {new Date(selectedAlarm.triggeredAt).toLocaleString()}
                    </span>
                  </div>
                  {selectedAlarm.acknowledgedAt && (
                    <div className="ald-detail-item">
                      <span className="ald-detail-label">Acknowledged At</span>
                      <span className="ald-detail-value">
                        {new Date(selectedAlarm.acknowledgedAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {selectedAlarm.resolvedAt && (
                    <div className="ald-detail-item">
                      <span className="ald-detail-label">Resolved At</span>
                      <span className="ald-detail-value">
                        {new Date(selectedAlarm.resolvedAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedAlarm.location && (
                <div className="ald-detail-section">
                  <h4>Location</h4>
                  <div className="ald-location-display">
                    <div className="ald-coords">
                      <MapPin size={16} />
                      <span>{selectedAlarm.location.latitude}, {selectedAlarm.location.longitude}</span>
                    </div>
                    {selectedAlarm.location.address && (
                      <p className="ald-address">{selectedAlarm.location.address}</p>
                    )}
                    <button 
                      className="ald-btn-secondary"
                      onClick={() => window.open(
                        `https://maps.google.com/?q=${selectedAlarm.location.latitude},${selectedAlarm.location.longitude}`,
                        '_blank'
                      )}
                    >
                      <ExternalLink size={14} />
                      Open in Maps
                    </button>
                  </div>
                </div>
              )}

              {selectedAlarm.data && Object.keys(selectedAlarm.data).length > 0 && (
                <div className="ald-detail-section">
                  <h4>Alarm Data</h4>
                  <div className="ald-detail-grid">
                    {selectedAlarm.data.speed && (
                      <div className="ald-detail-item">
                        <span className="ald-detail-label">Speed</span>
                        <span className="ald-detail-value">{selectedAlarm.data.speed} km/h</span>
                      </div>
                    )}
                    {selectedAlarm.data.speedLimit && (
                      <div className="ald-detail-item">
                        <span className="ald-detail-label">Speed Limit</span>
                        <span className="ald-detail-value">{selectedAlarm.data.speedLimit} km/h</span>
                      </div>
                    )}
                    {selectedAlarm.data.battery && (
                      <div className="ald-detail-item">
                        <span className="ald-detail-label">Battery</span>
                        <span className="ald-detail-value">{selectedAlarm.data.battery}%</span>
                      </div>
                    )}
                    {selectedAlarm.data.temperature && (
                      <div className="ald-detail-item">
                        <span className="ald-detail-label">Temperature</span>
                        <span className="ald-detail-value">{selectedAlarm.data.temperature}°C</span>
                      </div>
                    )}
                    {selectedAlarm.data.fuelLevel && (
                      <div className="ald-detail-item">
                        <span className="ald-detail-label">Fuel Level</span>
                        <span className="ald-detail-value">{selectedAlarm.data.fuelLevel}%</span>
                      </div>
                    )}
                    {selectedAlarm.data.geofenceName && (
                      <div className="ald-detail-item">
                        <span className="ald-detail-label">Geofence</span>
                        <span className="ald-detail-value">{selectedAlarm.data.geofenceName}</span>
                      </div>
                    )}
                    {selectedAlarm.data.dtcCodes?.length > 0 && (
                      <div className="ald-detail-item full">
                        <span className="ald-detail-label">DTC Codes</span>
                        <span className="ald-detail-value">{selectedAlarm.data.dtcCodes.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedAlarm.notes && (
                <div className="ald-detail-section">
                  <h4>Notes</h4>
                  <p className="ald-notes-text">{selectedAlarm.notes}</p>
                </div>
              )}
            </div>

            <div className="ald-modal-footer">
              {selectedAlarm.status === 'active' && (
                <button 
                  className="ald-btn-warning"
                  onClick={() => {
                    handleAcknowledge(selectedAlarm._id);
                    setShowAlarmDetailModal(false);
                  }}
                >
                  <CheckCircle size={16} />
                  Acknowledge
                </button>
              )}
              {selectedAlarm.status !== 'resolved' && (
                <button 
                  className="ald-btn-success"
                  onClick={() => {
                    handleResolve(selectedAlarm._id);
                    setShowAlarmDetailModal(false);
                  }}
                >
                  <XCircle size={16} />
                  Resolve
                </button>
              )}
              <button 
                className="ald-btn-secondary"
                onClick={() => setShowAlarmDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Modal */}
      {showBulkActionsModal && (
        <div className="ald-modal-backdrop" onClick={() => setShowBulkActionsModal(false)}>
          <div className="ald-modal-box small" onClick={e => e.stopPropagation()}>
            <div className="ald-modal-header">
              <h3>Bulk Actions</h3>
              <button className="ald-modal-close" onClick={() => setShowBulkActionsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="ald-bulk-content">
              <p>{selectedAlarms.length} alarms selected</p>
              <div className="ald-bulk-actions">
                <button 
                  className="ald-btn-warning"
                  onClick={handleBulkAcknowledge}
                >
                  <CheckCircle size={16} />
                  Acknowledge All
                </button>
                <button 
                  className="ald-btn-success"
                  onClick={handleBulkResolve}
                >
                  <XCircle size={16} />
                  Resolve All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="ald-modal-backdrop" onClick={() => setShowExportModal(false)}>
          <div className="ald-modal-box small" onClick={e => e.stopPropagation()}>
            <div className="ald-modal-header">
              <h3>Export Alarms</h3>
              <button className="ald-modal-close" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="ald-export-content">
              <p>Export {processedAlarms.length} alarms</p>
              <div className="ald-export-options">
                <button 
                  className="ald-export-btn"
                  onClick={() => handleExport('csv')}
                >
                  <FileText size={20} />
                  <span>Export as CSV</span>
                </button>
                <button 
                  className="ald-export-btn"
                  onClick={() => handleExport('json')}
                >
                  <Archive size={20} />
                  <span>Export as JSON</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlarmDashboard;
